// src/lib/purchaseOrderPdf.js
// Session 78n8.2, Issue #395. Gate decision D3.
//
// WHAT THIS IS: the PDF copy of the purchase order that travels attached to the
// email the vendor receives. D3's reasoning in one line -- a vendor's accounts
// department files a PDF, not an email body, so the order has to exist as a
// file even though the email already reads as the document.
//
// It draws from the SAME model src/lib/purchaseOrder.js builds. It does not
// re-derive a single number: no quantity is multiplied here, no total is summed
// here. That is deliberate and it is the whole reason the model exists -- a PDF
// that computed its own totals would be a second opinion about what D&R owes,
// and the vendor invoices against whichever copy they happened to receive.
//
// jsPDF is imported DYNAMICALLY, matching every other PDF path in this codebase
// (LedgerExportSheet.js, labelPdf.js, canonM5Export.js). It is a large library
// and the Purchasing Queue must not carry it in the main bundle for the sake of
// a button most page loads never press.

import { money, PO_ACCENT, purchaseOrderFilename, referenceInstruction } from './purchaseOrder';
import { addressLines } from './addresses';

// #EF9F27, the locked Purchasing colour, as the RGB triplet jsPDF wants.
const ACCENT_RGB = [239, 159, 39];
const INK_RGB = [26, 26, 26];
const MUTED_RGB = [107, 107, 107];

// jsPDF hands back an ArrayBuffer; Resend wants base64. Converted in chunks
// rather than with String.fromCharCode(...bytes) in one call, because spreading
// a multi-megabyte array into an argument list overflows the call stack -- the
// failure is a hard crash at send time on exactly the large orders that matter
// most, and it would never appear on a two-line test order.
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function drawAddressBlock(doc, heading, lines, x, y, width) {
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...MUTED_RGB);
  doc.text(String(heading).toUpperCase(), x, y);

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...INK_RGB);
  let cursor = y + 13;
  (lines.length ? lines : ['Address not set']).forEach(line => {
    const wrapped = doc.splitTextToSize(String(line), width);
    doc.text(wrapped, x, cursor);
    cursor += wrapped.length * 11;
  });
  return cursor;
}

// Builds the PDF and returns { base64, filename }. Does NOT save it -- the
// caller decides whether it is being attached to an email or handed to the
// browser, and `renderPurchaseOrderPdfDownload` below is the second case.
export async function renderPurchaseOrderPdf(order) {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.width;
  const M = 46;

  // ---- letterhead ---------------------------------------------------------
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK_RGB);
  doc.text(String(order.company?.name || ''), M, 58);

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED_RGB);
  let y = 72;
  addressLines(order.company, { includeName: false }).forEach(line => {
    doc.text(String(line), M, y);
    y += 11;
  });

  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK_RGB);
  doc.text('PURCHASE ORDER', W - M, 58, { align: 'right' });

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED_RGB);
  let ry = 72;
  const meta = [
    order.poRef ? `PO ${order.poRef}` : '',
    order.orderedOn ? `Ordered ${order.orderedOn}` : '',
    order.buyer?.name ? `Buyer: ${order.buyer.name}` : '',
  ].filter(Boolean);
  meta.forEach(line => { doc.text(line, W - M, ry, { align: 'right' }); ry += 11; });

  const ruleY = Math.max(y, ry) + 6;
  doc.setDrawColor(...ACCENT_RGB);
  doc.setLineWidth(2);
  doc.line(M, ruleY, W - M, ruleY);

  // ---- the three blocks ---------------------------------------------------
  const colW = (W - M * 2 - 24) / 3;
  const blockY = ruleY + 24;

  const vendorLines = [
    order.vendor?.name,
    order.vendor?.contactName ? `Attn: ${order.vendor.contactName}` : '',
    order.vendor?.email,
  ].filter(Boolean);

  const refLines = [
    order.requestNumber ? `Request ${order.requestNumber}` : '',
    order.workOrderRef ? `Job ${order.workOrderRef}` : '',
    order.vendor?.paymentTerms ? `Terms: ${order.vendor.paymentTerms}` : '',
  ].filter(Boolean);

  const b1 = drawAddressBlock(doc, 'Vendor', vendorLines, M, blockY, colW);
  const b2 = drawAddressBlock(doc, 'Ship to', addressLines(order.shipTo), M + colW + 12, blockY, colW);
  const b3 = drawAddressBlock(doc, 'Reference', refLines.length ? refLines : ['—'], M + (colW + 12) * 2, blockY, colW);

  // ---- the lines ----------------------------------------------------------
  autoTable(doc, {
    head: [['Item', 'SKU', 'Qty', 'Unit', 'Price / pack', 'Total']],
    body: order.lines.map(l => [
      l.name, l.sku, String(l.qty), l.unit, money(l.pricePerPack), money(l.total),
    ]),
    startY: Math.max(b1, b2, b3) + 18,
    margin: { left: M, right: M },
    styles: { fontSize: 9, cellPadding: 5, font: 'helvetica', textColor: INK_RGB },
    headStyles: { fillColor: INK_RGB, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: {
      2: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right', fontStyle: 'bold' },
    },
  });

  let afterTable = doc.lastAutoTable.finalY + 18;

  // ---- total --------------------------------------------------------------
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED_RGB);
  doc.text('ORDER TOTAL', W - M - 92, afterTable, { align: 'right' });

  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK_RGB);
  doc.text(money(order.orderTotal), W - M, afterTable, { align: 'right' });
  afterTable += 26;

  // ---- the buyer's note ---------------------------------------------------
  if (order.note) {
    const wrapped = doc.splitTextToSize(order.note, W - M * 2 - 20);
    const boxH = wrapped.length * 12 + 18;
    doc.setFillColor(255, 248, 236);
    doc.rect(M, afterTable, W - M * 2, boxH, 'F');
    doc.setFillColor(...ACCENT_RGB);
    doc.rect(M, afterTable, 3, boxH, 'F');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK_RGB);
    doc.text(wrapped, M + 12, afterTable + 16);
    afterTable += boxH + 20;
  }

  // ---- what the vendor writes on their paperwork --------------------------
  // Drawn as a bordered block rather than a footnote, deliberately: this is the
  // one instruction on the page that somebody in the vendor's warehouse has to
  // ACT on, and a grey sentence at the bottom of a page is what a picker skips.
  const refText = referenceInstruction(order);
  if (refText) {
    const wrapped = doc.splitTextToSize(refText, W - M * 2 - 24);
    const boxH = wrapped.length * 12 + 30;
    doc.setFillColor(255, 248, 236);
    doc.setDrawColor(...ACCENT_RGB);
    doc.setLineWidth(1);
    doc.rect(M, afterTable, W - M * 2, boxH, 'FD');

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(138, 90, 0);
    doc.text('ON YOUR PACKING SLIP AND INVOICE', M + 12, afterTable + 15);

    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK_RGB);
    doc.text(wrapped, M + 12, afterTable + 29);
    afterTable += boxH + 18;
  }

  // ---- the ask ------------------------------------------------------------
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED_RGB);
  doc.text('Please confirm receipt and expected ship date by reply.', M, afterTable);

  // ---- Page N of M --------------------------------------------------------
  // Same second pass LedgerExportSheet.js uses, and for the same reason: the
  // total is only knowable once the table has been laid out, and "Page 1" with
  // no total tells a reader holding a printout nothing about whether pages are
  // missing. On a document going to a supplier that matters more, not less.
  const pageCount = doc.internal.getNumberOfPages();
  const H = doc.internal.pageSize.height;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(160, 160, 160);
    doc.text(
      [order.company?.name, order.poRef ? `PO ${order.poRef}` : ''].filter(Boolean).join(' · '),
      M, H - 24
    );
    doc.text(`Page ${p} of ${pageCount}`, W - M, H - 24, { align: 'right' });
  }

  return {
    base64: arrayBufferToBase64(doc.output('arraybuffer')),
    filename: purchaseOrderFilename(order),
  };
}

// The same document, handed to the browser instead of to Resend. Used by the
// preview's "Download PDF" so Jennifer can see exactly the file the vendor will
// receive -- built by the same function, so a preview cannot disagree with the
// attachment.
export async function downloadPurchaseOrderPdf(order) {
  const { base64, filename } = await renderPurchaseOrderPdf(order);
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked on the next tick rather than immediately: Safari has a documented
  // habit of cancelling the download if the object URL dies in the same frame.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
